import { Component, type ErrorInfo, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, message: error.message };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Application error:", error, info.componentStack);
  }

  override render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="flex min-h-screen items-center justify-center bg-edlab-bg p-6">
            <div className="max-w-md rounded-2xl border border-edlab-line bg-white p-8 shadow-sm">
              <h1 className="text-xl font-semibold text-edlab-ink">
                Something went wrong
              </h1>
              <p className="mt-3 text-sm text-edlab-muted">
                {this.state.message ||
                  "An unexpected error occurred. Please refresh the page."}
              </p>
              <button
                type="button"
                className="mt-6 rounded-lg bg-edlab-green px-4 py-2 text-sm font-medium text-white hover:bg-edlab-green-dark"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Refresh page
              </button>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
